import React, { useState, useEffect, useLayoutEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Divider,
  CircularProgress,
  Alert,
  Grid
} from '@mui/material';
import useAuth from '../hooks/useAuth';
import useNotification from '../hooks/useNotification';
import useLanguage from '../hooks/useLanguage';
import { PageTitleContainer } from '../components/common';
import ReactSelect, { components } from 'react-select';

const Profile = () => {
  const { user, updateProfile, loading, error, refreshUserData } = useAuth();
  const { showNotification } = useNotification();
  const { t, currentLanguage, availableLanguages, changeLanguage } = useLanguage();
  const [isChangingLanguage, setIsChangingLanguage] = useState(false);

  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    language: currentLanguage || 'en'
  });

  // Update form data when user data is loaded
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || prev.name,
        email: user.email || prev.email
      }));
    }
  }, [user]);

  // Keep language in sync with currentLanguage
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      language: currentLanguage || 'en'
    }));
  }, [currentLanguage]);

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const [contentHeight, setContentHeight] = useState('calc(100vh - 130px)');

  // Calculate content height based on window size and header heights
  useLayoutEffect(() => {
    const updateContentHeight = () => {
      // Default header heights (fallback values if refs aren't available)
      const mainHeaderHeight = 73;  // Main app header
      const titleHeaderHeight = 80; // Create project header
      const extraPadding = 69;      // Additional padding for spacing

      const totalHeaderHeight = mainHeaderHeight + titleHeaderHeight + extraPadding;
      setContentHeight(`calc(100vh - ${totalHeaderHeight}px)`);
    };

    // Initial calculation
    updateContentHeight();

    // Re-calculate on window resize
    window.addEventListener('resize', updateContentHeight);

    return () => {
      window.removeEventListener('resize', updateContentHeight);
    };
  }, []);

  const validatePasswordChange = () => {
    const newErrors = {};

    if (formData.newPassword && !formData.currentPassword) {
      newErrors.currentPassword = t('settings.currentPasswordRequired');
    }

    if (formData.newPassword && formData.newPassword.length < 6) {
      newErrors.newPassword = t('settings.passwordMinLength');
    }

    if (formData.newPassword && formData.newPassword !== formData.confirmPassword) {
      newErrors.confirmPassword = t('settings.passwordsDoNotMatch');
    }

    return newErrors;
  };

  const handleLanguageChange = async (e) => {
    const newLanguage = e.target.value;

    // Start transition
    setIsChangingLanguage(true);

    // Update form data immediately
    setFormData({
      ...formData,
      language: newLanguage
    });

    // Small delay for fade effect
    setTimeout(async () => {
      // Change language in the UI
      await changeLanguage(newLanguage);

      // End transition after language change completes
      setTimeout(() => {
        setIsChangingLanguage(false);
      }, 100);
    }, 150);
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();

    // Validate if changing password
    if (formData.newPassword) {
      const validationErrors = validatePasswordChange();
      if (Object.keys(validationErrors).length > 0) {
        setErrors(validationErrors);
        return;
      }
    }

    try {
      const updateData = {
        name: formData.name,
        settings: {
          language: formData.language
        }
      };

      // Only include password fields if changing password
      if (formData.newPassword) {
        updateData.currentPassword = formData.currentPassword;
        updateData.newPassword = formData.newPassword;
      }

      console.log('Sending update data:', updateData);
      const updatedUser = await updateProfile(updateData);
      console.log('Received updated user:', updatedUser);

      // Update language if it was changed
      if (formData.language !== currentLanguage) {
        await changeLanguage(formData.language);
      }

      // Force update the form data with the returned user data
      if (updatedUser) {
        setFormData(prev => ({
          ...prev,
          name: updatedUser.name || prev.name,
          email: updatedUser.email || prev.email
        }));
      }

      // Refresh user data to ensure all components get the updated user
      if (refreshUserData) {
        console.log('Refreshing user data...');
        const refreshedUser = await refreshUserData();
        console.log('Refreshed user data:', refreshedUser);
      }

      showNotification({
        message: t('settings.updateSuccess'),
        type: 'success'
      });

      // Reset password fields
      setFormData({
        ...formData,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });

      setErrors({});
    } catch (err) {
      console.error('Error updating profile:', err);
    }
  };

  return (
    <Box sx={{
      width: '100%',
      minHeight: '100vh',
      backgroundColor: '#f5f7fa',
      px: 6,
      m: 0,
      pt: 4,
      overflowX: 'hidden',
      opacity: isChangingLanguage ? 0.3 : 1,
      transition: 'opacity 0.15s ease-in-out'
    }}>
      <PageTitleContainer
        title={t('settings.accountSettings')}
        sx={{
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          ml: 0,
          mr: 0,
          mb: 2,
          mt: 0,
          py: '16px'
        }}
        titleProps={{
          sx: { fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif", fontWeight: 700 }
        }}
      />

      <Box sx={{ mx: 'auto', pt: 0, width: '100%', height: contentHeight, overflowY: 'auto', boxShadow: '0 4px 20px rgba(0,0,0,0.05)', borderRadius: '4px' }}>
        <Paper elevation={0} sx={{
          p: 5, borderRadius: '4px', boxShadow: '0 4px 20px rgba(0,0,0,0.05) !important', height: '99%', overflow: 'auto',
          // Custom scrollbar styling
          '&::-webkit-scrollbar': {
            width: '8px',
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-track': {
            borderRadius: '10px',
            backgroundColor: 'rgba(0, 0, 0, 0.05)',
            marginTop: '5px',
            marginBottom: '5px',
          },
          '&::-webkit-scrollbar-thumb': {
            borderRadius: '10px',
            backgroundColor: 'rgba(0, 0, 0, 0.2)',
            '&:hover': {
              backgroundColor: 'rgba(25, 118, 210, 0.4)',
            },
          },
          '&::-webkit-scrollbar-corner': {
            backgroundColor: 'transparent',
          },
        }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleProfileUpdate}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif",
                fontWeight: 700,
                fontSize: '18px',
                color: '#475969',
                mb: 3
              }}
            >
              {t('settings.profile')}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 1,
                    fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif",
                    fontWeight: 700,
                    fontSize: '14px',
                    color: '#475969'
                  }}
                >
                  {t('auth.name')}
                </Typography>
                <TextField
                  fullWidth
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#3182CE',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#3182CE',
                      },
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '14px',
                      padding: '12px 14px',
                      color: 'var(--color-blue, #475969)',
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 1,
                    fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif",
                    fontWeight: 700,
                    fontSize: '14px',
                    color: '#475969'
                  }}
                >
                  {t('auth.email')}
                </Typography>
                <TextField
                  fullWidth
                  name="email"
                  value={formData.email}
                  disabled
                  helperText={t('settings.emailCannotBeChanged')}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-disabled': {
                        backgroundColor: '#f5f5f5',
                      },
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '14px',
                      padding: '12px 14px',
                      color: 'var(--color-blue, #475969)',
                    },
                    '& .MuiFormHelperText-root': {
                      marginLeft: '4px',
                      fontSize: '12px'
                    }
                  }}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif",
                fontWeight: 700,
                fontSize: '18px',
                color: '#475969',
                mb: 3
              }}
            >
              {t('settings.appearance')}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 1,
                    fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif",
                    fontWeight: 700
                  }}
                >
                  {t('settings.language')}
                </Typography>
                <ReactSelect
                  className="basic-single"
                  classNamePrefix="select"
                  name="language-preference"
                  inputId="language-preference-select"
                  instanceId="language-preference-instance"
                  value={availableLanguages.find(lang => lang.code === formData.language) || availableLanguages[0]}
                  onChange={(option) => handleLanguageChange({ target: { value: option ? option.code : 'en' } })}
                  options={availableLanguages}
                  getOptionLabel={(option) => option.name}
                  getOptionValue={(option) => option.code}
                  placeholder="Select language"
                  isClearable={false}
                  isDisabled={loading || isChangingLanguage}
                  isSearchable={false}
                  components={{
                    Input: (props) => (
                      <components.Input
                        {...props}
                        autoComplete="off"
                        aria-autocomplete="none"
                        data-form-type="other"
                        data-lpignore="true"
                      />
                    )
                  }}
                  styles={{
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      borderRadius: '4px',
                      minHeight: '45px',
                      borderColor: state.isFocused ? '#475969' : '#4759694d',
                      boxShadow: state.isFocused ? 'none !important' : 'none',
                      '&:hover': {
                        borderColor: '#475969'
                      }
                    }),
                    placeholder: (baseStyles) => ({
                      ...baseStyles,
                      color: '#a2abb3',
                      opacity: 0.7,
                      fontSize: "14px !important"
                    }),
                    singleValue: (baseStyles) => ({
                      ...baseStyles,
                      fontSize: "14px !important",
                      color: 'var(--color-blue, #475969)'
                    }),
                    menu: (baseStyles) => ({
                      ...baseStyles,
                      zIndex: 9999
                    }),
                    menuPortal: (baseStyles) => ({
                      ...baseStyles,
                      zIndex: 9999
                    })
                  }}
                  menuPortalTarget={document.body}
                />
              </Grid>
            </Grid>

            <Divider sx={{ my: 4 }} />

            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif",
                fontWeight: 700,
                fontSize: '18px',
                color: '#475969',
                mb: 3
              }}
            >
              {t('auth.resetPassword')}
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 1,
                    fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif",
                    fontWeight: 700,
                    fontSize: '14px',
                    color: '#475969'
                  }}
                >
                  {t('settings.currentPassword')}
                </Typography>
                <TextField
                  fullWidth
                  name="currentPassword"
                  type="password"
                  value={formData.currentPassword}
                  onChange={handleChange}
                  error={!!errors.currentPassword}
                  helperText={errors.currentPassword}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#3182CE',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#3182CE',
                      },
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '14px',
                      padding: '12px 14px',
                      color: 'var(--color-blue, #475969)',
                    },
                    '& .MuiFormHelperText-root': {
                      marginLeft: '4px',
                      fontSize: '12px'
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 1,
                    fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif",
                    fontWeight: 700,
                    fontSize: '14px',
                    color: '#475969'
                  }}
                >
                  {t('settings.newPassword')}
                </Typography>
                <TextField
                  fullWidth
                  name="newPassword"
                  type="password"
                  value={formData.newPassword}
                  onChange={handleChange}
                  error={!!errors.newPassword}
                  helperText={errors.newPassword}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#3182CE',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#3182CE',
                      },
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '14px',
                      padding: '12px 14px',
                      color: 'var(--color-blue, #475969)',
                    },
                    '& .MuiFormHelperText-root': {
                      marginLeft: '4px',
                      fontSize: '12px'
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography
                  variant="body1"
                  sx={{
                    mb: 1,
                    fontFamily: "'Sofia Pro Bold', 'Sofia Pro', sans-serif",
                    fontWeight: 700,
                    fontSize: '14px',
                    color: '#475969'
                  }}
                >
                  {t('settings.confirmNewPassword')}
                </Typography>
                <TextField
                  fullWidth
                  name="confirmPassword"
                  type="password"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  error={!!errors.confirmPassword}
                  helperText={errors.confirmPassword}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      '&:hover fieldset': {
                        borderColor: '#3182CE',
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#3182CE',
                      },
                    },
                    '& .MuiInputBase-input': {
                      fontSize: '14px',
                      padding: '12px 14px',
                      color: 'var(--color-blue, #475969)',
                    },
                    '& .MuiFormHelperText-root': {
                      marginLeft: '4px',
                      fontSize: '12px'
                    }
                  }}
                />
              </Grid>
            </Grid>

            <Box sx={{ mt: 4, textAlign: 'right' }}>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{
                  backgroundColor: '#3182CE',
                  color: 'white',
                  textTransform: 'none',
                  fontSize: '14px',
                  fontWeight: 500,
                  padding: '10px 24px',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                  '&:hover': {
                    backgroundColor: '#2C5282',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                  },
                  '&:disabled': {
                    backgroundColor: '#E2E8F0',
                    color: '#A0AEC0',
                  }
                }}
              >
                {loading ? <CircularProgress size={20} sx={{ color: 'white' }} /> : t('common.save')}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default Profile;